import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SingleImageUpload } from '../../components/ui/image-upload';
import { categoriesAPI } from '../../lib/api';
import CategoryFilters, { useCategoryFilters } from '../../components/CategoryFilters';
import { cn, getImageUrl } from '../../lib/utils';
import { toast } from 'sonner';
import { fetchWithCache } from '../../lib/cache';
import { Plus, Pencil, Trash2, FolderTree, ChevronRight, Folder, FolderOpen, RefreshCcw } from 'lucide-react';
import { usePopup } from '../../contexts/PopupContext';

export default function AdminCategories() {
  const categoryFilters = useCategoryFilters();
  const [categories, setCategories] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'flat'
  const [expanded, setExpanded] = useState({});
  const { showPopup } = usePopup();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    parent_id: '',
    is_active: true,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      await fetchWithCache(
        'admin_categories_data',
        async () => {
          const [treeResponse, flatResponse] = await Promise.all([
            categoriesAPI.getTree(),
            categoriesAPI.getFlat()
          ]);
          return { tree: treeResponse.data || [], flat: flatResponse.data || [] };
        },
        (data, isFromCache) => {
          setCategories(data.tree);
          setFlatCategories(data.flat);
          // Auto-expand top level categories by default if desired, or keep collapsed
          if (isFromCache) setLoading(false);
        }
      );
    } catch (error) {
      console.error('Failed to fetch categories', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (categoryId) => {
    setExpanded(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      image_url: '',
      parent_id: '',
      is_active: true,
      sort_order: '0',
    });
    setEditingCategory(null);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      image_url: category.image_url || '',
      parent_id: category.parent_id || '',
      is_active: category.is_active,
      sort_order: String(category.sort_order || 0),
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const submitData = {
        ...formData,
        parent_id: formData.parent_id || null
      };

      if (editingCategory) {
        await categoriesAPI.update(editingCategory.id, submitData);
        toast.success('Category updated successfully!');
      } else {
        await categoriesAPI.create(submitData);
        toast.success('Category created successfully!');
      }
      setShowDialog(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDelete = async (id, hasChildren = false, productsCount = 0) => {
    const warningMessage = hasChildren || productsCount > 0
      ? `This category has ${hasChildren ? 'subcategories' : ''} ${hasChildren && productsCount > 0 ? 'and' : ''} ${productsCount > 0 ? `${productsCount} products` : ''}. This will delete everything permanently.`
      : "Are you sure you want to delete this category?";

    showPopup({
      title: "Delete Category",
      message: warningMessage,
      type: "error",
      onConfirm: async () => {
        try {
          await categoriesAPI.delete(id, hasChildren || productsCount > 0);
          toast.success('Category deleted');
          fetchCategories();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to delete category');
        }
      }
    });
  };

  const renderCategoryTree = (categories, level = 0) => {
    return categories.map((category) => {
      const hasChildren = category.children && category.children.length > 0;
      const isExpanded = expanded[category.id] || Boolean(categoryFilters.categoryIds);

      return (
        <React.Fragment key={category.id}>
          <TableRow className="border-slate-700">
            <TableCell>
              <div className="flex items-center gap-2" style={{ paddingLeft: `${level * 24}px` }}>
                {hasChildren ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 p-0 hover:bg-slate-700/50"
                    onClick={() => toggleExpand(category.id)}
                  >
                    <ChevronRight className={cn("w-4 h-4 text-slate-500 transition-transform duration-200", isExpanded && "rotate-90")} />
                  </Button>
                ) : (
                  <div className="w-6" /> // Spacer
                )}

                {hasChildren && isExpanded ? (
                  <FolderOpen className="w-4 h-4 text-blue-400" />
                ) : (
                  <Folder className="w-4 h-4 text-slate-400" />
                )}

                <div className="w-8 h-8 bg-slate-700 rounded-lg overflow-hidden ml-1">
                  {category.image_url && (
                    <img src={getImageUrl(category.image_url)} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <div>
                  <span className="font-medium cursor-pointer" onClick={() => hasChildren && toggleExpand(category.id)}>{category.name}</span>
                  {level > 0 && (
                    <div className="text-xs text-slate-500">
                      ID: {category.id.substring(0, 8)}...
                    </div>
                  )}
                </div>
              </div>
            </TableCell>
            <TableCell>
              <code className="text-xs bg-slate-950 px-2 py-1 rounded text-slate-400 font-mono select-all">
                {category.id}
              </code>
            </TableCell>
            <TableCell>
              <span className="text-slate-400 font-mono text-xs">{category.sort_order}</span>
            </TableCell>
            <TableCell className="text-slate-400 max-w-[300px] truncate">
              {category.description || '-'}
            </TableCell>
            <TableCell>
              <div className="flex gap-2">
                <Badge className={category.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}>
                  {category.is_active ? 'Active' : 'Inactive'}
                </Badge>
                {hasChildren && (
                  <Badge variant="outline" className="text-blue-400 border-blue-400/30">
                    {category.children.length} sub
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-2">
                <Button size="icon" variant="ghost" onClick={() => handleEdit(category)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                  onClick={() => handleDelete(
                    category.id,
                    hasChildren,
                    category.products_count || 0
                  )}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
          {hasChildren && isExpanded && renderCategoryTree(category.children, level + 1)}
        </React.Fragment>
      );
    });
  };

  const renderFlatTable = () => {
    return flatCategories.filter(category => !categoryFilters.categoryIds || categoryFilters.categoryIds.has(String(category.id))).map((category) => (
      <TableRow key={category.id} className="border-slate-700">
        <TableCell>
          <div className="flex items-center gap-3" style={{ paddingLeft: `${category.level * 20}px` }}>
            {category.level > 0 && <ChevronRight className="w-4 h-4 text-slate-500" />}
            <div className="w-8 h-8 bg-slate-700 rounded-lg overflow-hidden">
              {category.image_url && (
                <img src={getImageUrl(category.image_url)} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <div>
              <span className="font-medium">{category.name}</span>
              <div className="text-xs text-slate-500">
                {category.full_path}
              </div>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <code className="text-xs bg-slate-950 px-2 py-1 rounded text-slate-400 font-mono select-all">
            {category.id}
          </code>
        </TableCell>
        <TableCell>
          <span className="text-slate-400 font-mono text-xs">{category.sort_order}</span>
        </TableCell>
        <TableCell className="text-slate-400 max-w-[300px] truncate">
          {category.description || '-'}
        </TableCell>
        <TableCell>
          <div className="flex gap-2">
            <Badge className={category.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}>
              {category.is_active ? 'Active' : 'Inactive'}
            </Badge>
            <Badge variant="outline" className="text-slate-400">
              L{category.level}
            </Badge>
            {category.has_children && (
              <Badge variant="outline" className="text-blue-400 border-blue-400/30">
                Has children
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-2">
            <Button size="icon" variant="ghost" onClick={() => handleEdit(category)}>
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="text-red-400 hover:text-red-300"
              onClick={() => handleDelete(category.id, category.has_children, category.products_count || 0)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="space-y-6" data-testid="admin-categories">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Categories</h1>
          <p className="text-slate-400">{flatCategories.length} categories</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={async () => {
              setRefreshing(true);
              await fetchCategories();
              setRefreshing(false);
            }}
            className="btn-admin"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tree">Tree View</SelectItem>
              <SelectItem value="flat">Flat View</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-admin bg-primary hover:bg-primary/90" data-testid="add-category-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700">
              <DialogHeader>
                <DialogTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Category Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter category name"
                    className="input-admin"
                    required
                    data-testid="category-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Parent Category</Label>
                  <Select
                    value={formData.parent_id || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, parent_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger className="input-admin">
                      <SelectValue placeholder="Select parent category (optional)" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="none">No Parent (Root Category)</SelectItem>
                      {flatCategories
                        .filter(cat => {
                          // Prevent selecting self
                          if (cat.id === editingCategory?.id) return false;

                          // Prevent selecting own descendants (Cycle protection)
                          if (editingCategory) {
                            let current = cat;
                            // Traverse up from the candidate parent
                            while (current && current.parent_id) {
                              if (current.parent_id === editingCategory.id) return false;
                              // find parent in flat list
                              const parentId = current.parent_id;
                              current = flatCategories.find(c => c.id === parentId);
                            }
                          }
                          return true;
                        })
                        .map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            <span className="flex items-center gap-2">
                              {/* Indentation visual */}
                              <span style={{ width: `${category.level * 10}px`, display: 'inline-block' }}></span>
                              {category.name}
                            </span>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400">
                    Change the parent category to move this category in the hierarchy.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                    placeholder="0"
                    className="input-admin"
                  />
                  <p className="text-xs text-slate-400">
                    Lower numbers appear first. Negative numbers allowed.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Category description"
                    className="input-admin min-h-[80px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Category Image</Label>
                  <SingleImageUpload
                    value={formData.image_url}
                    onChange={(imageUrl) => setFormData({ ...formData, image_url: imageUrl })}
                    folder="categories"
                    label=""
                    description="Upload category image (max 5MB)"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label>Active</Label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setShowDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="save-category-btn">
                    {editingCategory ? 'Update Category' : 'Create Category'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Categories Table */}
      <CategoryFilters filters={{ ...categoryFilters, categories: flatCategories }} />
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-400">Category</TableHead>
                <TableHead className="text-slate-400">ID</TableHead>
                <TableHead className="text-slate-400">Order</TableHead>
                <TableHead className="text-slate-400">Description</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : flatCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                    <FolderTree className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    No categories found
                  </TableCell>
                </TableRow>
              ) : viewMode === 'tree' ? (
                renderCategoryTree(categoryFilters.categoryIds
                  ? flatCategories.filter(category => String(category.id) === (categoryFilters.subcategory || categoryFilters.category))
                    .map(category => {
                      const findNode = nodes => nodes.reduce((found, node) => found || (String(node.id) === String(category.id) ? node : findNode(node.children || [])), null);
                      return findNode(categories) || category;
                    })
                  : categories)
              ) : (
                renderFlatTable()
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
